<script setup>
import { Head } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import ManagementHeader from "../../../Components/Management/ManagementHeader.vue";
import MonthlySpecialDayCalendar from "../../../Components/Management/MonthlySpecialDayCalendar.vue";
import SpecialDayEditorModal from "../../../Components/Management/SpecialDayEditorModal.vue";
import Dropdown from "primevue/dropdown";
import { ref, watch } from "vue";
import { Inertia } from "@inertiajs/inertia";

const props = defineProps([
    'providers',
    'selectedProvider',
    'filters',
    'flash'
]);

const route = window.route;
const selectedProviderUuid = ref(props.filters?.provider);
const calendarRef = ref(null);

// Modal State
const editorVisible = ref(false);
const selectedDate = ref('');
const editingRecord = ref(null);
const defaultHoursForSelectedDate = ref(null);

const handleProviderChange = (e) => {
    selectedProviderUuid.value = e.value;
    Inertia.get(route('management.providers.special-days'), { provider: e.value }, {
        preserveState: false,
        preserveScroll: true,
        only: ['selectedProvider', 'filters']
    });
};

const openEditor = (dateStr) => {
    selectedDate.value = dateStr;
    
    // Find existing special day record
    editingRecord.value = props.selectedProvider.special_days?.find(d => {
        const start = (d.start_date || d.date || '').split('T')[0];
        const end = (d.end_date || start || '').split('T')[0];
        return dateStr >= start && dateStr <= end;
    }) || null;
    
    // Fallback logic: Provider Working Hour -> Company Working Hour
    const date = new Date(dateStr);
    const dow = date.getDay();
    
    const providerDefault = props.selectedProvider.working_hours?.find(h => h.day_of_week === dow);
    const companyDefault = props.selectedProvider.company?.working_hours?.find(h => h.day_of_week === dow);
    
    defaultHoursForSelectedDate.value = providerDefault || companyDefault || null;
    
    editorVisible.value = true;
};

const onSaved = () => {
    Inertia.reload({
        only: ['selectedProvider'],
        onSuccess: () => {
            if (calendarRef.value) calendarRef.value.refresh();
        }
    });
};

const onCleared = () => {
    Inertia.reload({
        only: ['selectedProvider'],
        onSuccess: () => {
            if (calendarRef.value) calendarRef.value.refresh();
        }
    });
};
</script>

<template>
    <Layout :flash="flash">
        <Head title="Sondertage Hub" />

        <div class="space-y-6">
            <ManagementHeader title="Mitarbeiter Management" module="providers" :activeIndex="2" />

            <!-- Top Selector Section -->
            <div class="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
                        <i class="pi pi-users text-gray-400" />
                    </div>
                    <div>
                        <h3 class="font-bold text-gray-700">Mitarbeiter wählen</h3>
                        <p class="text-xs text-gray-400">Ausnahmen und Sondertage für Teammitglieder verwalten</p>
                    </div>
                </div>
                
                <div class="w-full md:w-80">
                    <Dropdown 
                        v-model="selectedProviderUuid" 
                        @change="handleProviderChange"
                        :options="providers" 
                        optionLabel="name" 
                        optionValue="uuid" 
                        placeholder="Mitarbeiter suchen..." 
                        filter 
                        class="w-full rounded-xl border-gray-200"
                    >
                        <template #option="slotProps">
                            <div class="flex items-center gap-2">
                                <span class="font-medium">{{ slotProps.option.name }}</span>
                            </div>
                        </template>
                    </Dropdown>
                </div>
            </div>

            <!-- Content Area -->
            <div v-if="selectedProvider" class="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div class="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                    <!-- Provider Info Bar -->
                    <div class="px-8 py-6 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
                        <div class="flex items-center gap-4">
                            <div class="w-12 h-12 rounded-2xl bg-black flex items-center justify-center text-[#FFF053] font-bold text-xl">
                            {{ selectedProvider.name.charAt(0) }}
                        </div>
                        <div>
                                <h2 class="text-xl font-bold text-gray-800">{{ selectedProvider.name }}</h2>
                                <p class="text-sm text-gray-400">Sondertage & Abwesenheiten</p>
                            </div>
                        </div>
                    </div>

                    <div class="px-6 pb-6">
                        <MonthlySpecialDayCalendar 
                            ref="calendarRef"
                            type="provider"
                            :companyId="selectedProvider.company_id"
                            :providerId="selectedProvider.id"
                            title="Sondertage & Ausnahmen"
                            @date-selected="openEditor"
                        />
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else class="py-20 text-center bg-gray-50/50 rounded-3xl border border-dashed border-gray-200">
                <div class="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-4">
                    <i class="pi pi-search text-2xl text-gray-300" />
                </div>
                <h3 class="text-lg font-bold text-gray-400">Kein Mitarbeiter ausgewählt</h3>
                <p class="text-gray-300">Bitte wählen Sie oben einen Mitarbeiter aus, um Sondertage zu bearbeiten.</p>
            </div>
        </div>

        <!-- Editor Modal -->
        <SpecialDayEditorModal 
            v-if="selectedProvider"
            v-model:visible="editorVisible"
            :date="selectedDate"
            type="provider"
            :companyId="selectedProvider.company_id"
            :providerId="selectedProvider.id"
            :existing="editingRecord"
            :defaultHours="defaultHoursForSelectedDate"
            @saved="onSaved"
            @cleared="onCleared"
        />
    </Layout>
</template>
